import { useEffect, useRef, useState } from 'react';
import { formatAutoLogout, formatCountdown } from '@ehr/shared';
import { Modal } from './Modal.js';
import { api } from '../lib/api.js';
import { useLiveRefresh } from '../lib/useLiveRefresh.js';
import {
  IdleSession,
  LOGOUT_KEY,
  announceSignOut,
  autosaveBeforeSignOut,
  markSignedOut,
  type SignOutReason,
} from '../lib/idleSession.js';
import { endSession, signInAgainUrl } from '../lib/signOut.js';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];
/** Other tabs get this long to save their own work before the session ends. */
const MIN_SAVE_WINDOW_MS = 1500;

const storage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/**
 * ehr#416 (ADMIN_SETTINGS_10-13-V1) - auto logout. After the person's time with no activity in
 * any EHR tab: "Are you still there?" with a 2:00 countdown, then open editors save their work,
 * the Credify sign-in ends (every module) and every EHR tab shows the signed-out screen.
 * The time comes from Data & settings > Auto logout and is re-read every 5 minutes and on focus.
 */
export function AutoLogoutGuard({
  isProd,
  onSignOut,
}: {
  isProd: boolean;
  /** The menu's Log out (signs out and goes to the sign-in page). */
  onSignOut: () => Promise<void>;
}) {
  const [minutes, setMinutes] = useState<number | null>(null);
  const [warn, setWarn] = useState<number | null>(null);
  const [out, setOut] = useState<{ reason: SignOutReason; saved: string[] } | null>(null);
  const [leaving, setLeaving] = useState(false);
  const session = useRef<IdleSession | null>(null);
  const outRef = useRef(false);

  const load = () => {
    api
      .get<{ minutes: number }>('/auto-logout/mine')
      .then((r) => setMinutes(r.minutes))
      .catch(() => undefined); // keep the last known time; none yet means no clock
  };
  useEffect(load, []);
  useLiveRefresh(load, 5 * 60_000);

  const signOut = async (reason: SignOutReason, announce: boolean) => {
    if (outRef.current) return;
    outRef.current = true;
    markSignedOut();
    setWarn(null);
    // Tell the other tabs first, so they save their own work while the sign-in still holds.
    if (announce) announceSignOut(storage(), reason);
    const started = Date.now();
    const saved = await autosaveBeforeSignOut(window);
    if (announce) {
      const wait = MIN_SAVE_WINDOW_MS - (Date.now() - started);
      if (wait > 0) await new Promise((r) => setTimeout(r, wait));
      let local: Storage | undefined;
      let sess: Storage | undefined;
      try {
        local = window.localStorage;
        sess = window.sessionStorage;
      } catch {
        /* storage blocked */
      }
      await endSession({
        postLogout: () => api.post('/auth/logout'),
        fetchFn: (...a) => fetch(...a),
        storage: { local, session: sess },
      });
    }
    setOut({ reason, saved });
  };

  // A new time (an admin changed it) applies at once, without restarting the clock.
  const minutesRef = useRef<number | null>(null);
  useEffect(() => {
    minutesRef.current = minutes;
    if (minutes != null) session.current?.setMinutes(minutes);
  }, [minutes]);

  // The clock: one per tab, built once the time is known, sharing activity through localStorage.
  const ready = minutes != null;
  useEffect(() => {
    if (!ready || minutesRef.current == null) return;
    const s = new IdleSession(minutesRef.current, storage());
    session.current = s;
    const onActivity = () => {
      if (!outRef.current) s.activity();
    };
    for (const t of ACTIVITY_EVENTS)
      document.addEventListener(t, onActivity, { capture: true, passive: true });
    const timer = window.setInterval(() => {
      if (outRef.current) return;
      const st = s.tick();
      if (st.kind === 'expired') void signOut('idle', true);
      else setWarn(st.kind === 'warn' ? st.remaining : null);
    }, 1000);
    return () => {
      for (const t of ACTIVITY_EVENTS) document.removeEventListener(t, onActivity, true);
      window.clearInterval(timer);
      session.current = null;
    };
    // signOut reads refs only; the clock is built once and takes new times via setMinutes.
  }, [ready]);

  // Signed out in another EHR tab (Log out, or its own clock ran out).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === LOGOUT_KEY && e.newValue) void signOut('other-tab', false);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  if (out) {
    const title =
      out.reason === 'idle'
        ? 'You were signed out for inactivity'
        : out.reason === 'other-tab'
          ? 'You were signed out in another tab'
          : 'You signed out';
    return (
      <div
        className="auto-logout-screen"
        role="dialog"
        aria-modal="true"
        aria-labelledby="alo-title"
      >
        <div className="auto-logout-card">
          <span className="auto-logout-brand">Credify</span>
          <h2 id="alo-title">{title}</h2>
          {out.reason === 'idle' && minutes != null && (
            <p>
              For security, Credify signs you out after {formatAutoLogout(minutes)} with no
              activity.
            </p>
          )}
          {out.saved.length > 0 && (
            <p>
              Your unsaved {out.saved.join(' and ')} {out.saved.length === 1 ? 'was' : 'were'} saved
              as a draft.
            </p>
          )}
          <button
            className="btn primary"
            onClick={() => window.location.assign(signInAgainUrl(isProd, window.location.href))}
          >
            Sign In Again
          </button>
        </div>
      </div>
    );
  }

  if (warn == null) return null;
  const stay = () => {
    session.current?.stay();
    setWarn(null);
  };
  return (
    <Modal
      title="Are you still there?"
      onClose={stay}
      className="auto-logout-warn"
      actions={
        <>
          <button
            className="btn"
            disabled={leaving}
            onClick={() => {
              setLeaving(true);
              outRef.current = true;
              markSignedOut();
              announceSignOut(storage(), 'manual');
              void autosaveBeforeSignOut(window).finally(() => void onSignOut());
            }}
          >
            {leaving ? 'Signing out…' : 'Sign Out'}
          </button>
          <button className="btn primary" onClick={stay} disabled={leaving}>
            Stay Signed In
          </button>
        </>
      }
    >
      <p className="auto-logout-line" role="timer" aria-live="polite">
        You will be signed out in{' '}
        <span className="auto-logout-countdown">{formatCountdown(warn)}</span>
      </p>
    </Modal>
  );
}
